import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Cable,
  ClipboardCheck,
  Phone,
  ArrowRight,
  Wrench,
  MapPin,
  Award,
  ShieldCheck,
  Timer,
  FileSearch,
  HardHat,
} from "lucide-react";
import Seo from "@/components/Seo";
import CtaBand from "@/components/CtaBand";

const HERO_IMG = "/realisations/intervention-facade.webp";
const FIBER_IMG =
  "https://images.unsplash.com/photo-1594915440248-1e419eba6611?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxmaWJlciUyMG9wdGljJTIwc3BsaWNpbmd8ZW58MHx8fHwxNzkyOTU3NzcwfDA&ixlib=rb-4.1.0&q=85";
const QUALITY_IMG =
  "https://images.unsplash.com/photo-1700727448575-6f1680cd7d75?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxxdWFsaXR5JTIwaW5zcGVjdGlvbiUyMGNsaXBib2FyZHxlbnwwfHx8fDE3OTI5NTc3NzB8MA&ixlib=rb-4.1.0&q=85";
const INFRA_IMG =
  "https://images.unsplash.com/photo-1582193607281-dafb7941640f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwzfHx0ZWxlY29tJTIwaW5mcmFzdHJ1Y3R1cmV8ZW58MHx8fHwxNzkyOTU3NzcwfDA&ixlib=rb-4.1.0&q=85";

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const VALUES = [
  { icon: Award, title: "Expertise technique", text: "Des techniciens formés aux infrastructures fibre optique et aux référentiels télécoms." },
  { icon: HardHat, title: "Présence sur le terrain", text: "Des équipes d'intervention qui opèrent au plus près des infrastructures, partout en France." },
  { icon: ShieldCheck, title: "Qualité d'exécution", text: "Des travaux réalisés dans les règles de l'art, avec un niveau d'exigence constant." },
  { icon: ClipboardCheck, title: "Rigueur & conformité", text: "Chaque contrôle suit un protocole documenté, conforme aux exigences du donneur d'ordre." },
  { icon: Timer, title: "Réactivité", text: "Une organisation agile, capable de mobiliser rapidement les bonnes compétences." },
  { icon: FileSearch, title: "Traçabilité des contrôles", text: "Des remontées terrain documentées et exploitables pour piloter la qualité des travaux." },
];

const Home = () => (
  <>
    <Seo
      title="ATLAS THD — Sous-traitant fibre optique & raccordement FTTH"
      description="Entreprise fibre optique : sous-traitance FTTH, raccordement FTTH D3, installation, maintenance et contrôle qualité des réseaux. Interventions en France."
    />

    {/* HERO */}
    <section className="border-b border-line bg-cloud" data-testid="hero-section">
      <div className="bg-blueprint">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 pb-20 pt-16 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:pb-28 lg:pt-24 lg:px-8">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 font-display text-xs font-bold uppercase tracking-[0.25em] text-steel"
              data-testid="hero-overline"
            >
              <span className="text-ciel">Raccordement fibre optique</span>
              <span className="text-line">/</span>
              <span className="text-qualite">Contrôle qualité</span>
            </motion.p>
            <h1 className="mt-6 font-display text-[clamp(2.5rem,5vw,4rem)] font-extrabold leading-[1.05] tracking-tight text-midnight">
              {["CONNECTER.", "CONTRÔLER.", "FIABILISER."].map((word, i) => (
                <motion.span
                  key={word}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.15 + i * 0.15 }}
                  className="block"
                >
                  {word}
                </motion.span>
              ))}
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="mt-6 max-w-xl text-lg leading-relaxed text-steel"
              data-testid="hero-description"
            >
              ATLAS THD accompagne les acteurs des télécommunications dans le
              raccordement des infrastructures fibre optique, l'inspection
              technique et le contrôle qualité, avec une approche fondée sur la
              rigueur, la fiabilité et la maîtrise du terrain.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.75 }}
              className="mt-8 flex flex-col gap-3 sm:flex-row"
            >
              <Link
                to="/contact"
                data-testid="hero-devis-button"
                className="group flex items-center justify-center gap-2 bg-ciel px-6 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-midnight transition-colors duration-200 hover:bg-midnight hover:text-white"
              >
                Parlez-nous de votre besoin
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
              <a
                href="tel:+33605765066"
                data-testid="hero-call-button"
                className="flex items-center justify-center gap-2 border border-midnight px-6 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-midnight transition-colors duration-200 hover:bg-midnight hover:text-white"
              >
                <Phone className="h-4 w-4" />
                06 05 76 50 66
              </a>
            </motion.div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.9 }}
              className="mt-6 flex items-center gap-2 text-sm text-steel"
            >
              <MapPin className="h-4 w-4 text-terrain" />
              Interventions techniques sur toute la France
            </motion.p>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="relative"
          >
            <div className="border border-line bg-white p-3">
              <img
                src={HERO_IMG}
                alt="Technicien en nacelle intervenant sur une façade en milieu urbain"
                className="aspect-[4/3] w-full object-cover"
                data-testid="hero-image"
              />
            </div>
            <div className="absolute -bottom-5 -left-5 hidden border border-line bg-white px-5 py-4 sm:block" data-testid="hero-badge-terrain">
              <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-terrain">Le Déploiement</p>
              <p className="mt-1 font-display text-lg font-extrabold text-midnight">FTTO / FTTH</p>
            </div>
            <div className="absolute -right-5 -top-5 hidden max-w-[280px] border border-line bg-white px-5 py-4 sm:block" data-testid="hero-badge-qualite">
              <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-qualite">L'Audit</p>
              <p className="mt-1 text-xs font-semibold leading-snug text-midnight">Contrôlez et auditez la qualité et la conformité de vos réseaux, pour une infrastructure plus fiable et durable.</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>

    {/* DOUBLE ACTIVITÉ */}
    <section className="bg-white py-20 lg:py-28" data-testid="activities-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeUp} transition={{ duration: 0.5 }} className="max-w-2xl">
          <p className="font-display text-xs font-bold uppercase tracking-[0.25em] text-steel">
            Nos deux métiers
          </p>
          <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-midnight sm:text-4xl lg:text-5xl">
            Deux expertises. Une même exigence.
          </h2>
          <p className="mt-4 text-lg text-steel">
            ATLAS THD réalise les travaux de raccordement fibre optique et
            contrôle la qualité des travaux réalisés : une double compétence
            complémentaire.
          </p>
        </motion.div>
        <div className="mt-12 grid gap-px border border-line bg-line lg:grid-cols-2">
          <motion.article
            {...fadeUp}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="group flex flex-col bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_-16px_rgba(10,25,47,0.25)] lg:p-12"
            data-testid="activity-card-terrain"
          >
            <div className="h-1.5 w-16 bg-terrain" />
            <div className="mt-8 flex items-center justify-between">
              <span className="font-display text-sm font-bold uppercase tracking-[0.25em] text-steel">Activité 01</span>
              <Cable className="h-8 w-8 text-terrain" />
            </div>
            <h3 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-midnight lg:text-4xl">
              LE DÉPLOIEMENT
            </h3>
            <p className="mt-4 text-lg leading-relaxed text-steel">
              Raccordement fibre optique FTTO et FTTH, interventions techniques
              et infrastructures télécoms.
            </p>
            <ul className="mt-8 space-y-3 border-t border-line pt-8 text-sm font-medium text-midnight">
              {["Raccordement fibre optique FTTO", "Raccordement FTTH (segment D3)", "Étude technique dédiée aux professionnels", "Maintenance, dépannage et fiabilisation des réseaux fibre optique professionnels", "Travaux et dépose des réseaux cuivre"].map((item) => (
                <li key={item} className="flex items-center gap-3">
                  <span className="h-2 w-2 bg-terrain" />
                  {item}
                </li>
              ))}
            </ul>
            <Link
              to="/terrain-fibre"
              data-testid="activity-terrain-link"
              className="mt-10 flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-terrain transition-colors group-hover:text-midnight"
            >
              Découvrir l'activité terrain
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </motion.article>
          <motion.article
            {...fadeUp}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="group flex flex-col bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_-16px_rgba(10,25,47,0.25)] lg:p-12"
            data-testid="activity-card-qualite"
          >
            <div className="h-1.5 w-16 bg-qualite" />
            <div className="mt-8 flex items-center justify-between">
              <span className="font-display text-sm font-bold uppercase tracking-[0.25em] text-steel">Activité 02</span>
              <ClipboardCheck className="h-8 w-8 text-qualite" />
            </div>
            <h3 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-midnight lg:text-4xl">
              L'AUDIT
            </h3>
            <p className="mt-4 text-lg leading-relaxed text-steel">
              Inspection, contrôle et audit des travaux fibre optique.
            </p>
            <ul className="mt-8 space-y-3 border-t border-line pt-8 text-sm font-medium text-midnight">
              {["Inspection technique des travaux", "Contrôle de conformité", "Audits qualité", "Suivi QHSE et remontées VQSE"].map((item) => (
                <li key={item} className="flex items-center gap-3">
                  <span className="h-2 w-2 bg-qualite" />
                  {item}
                </li>
              ))}
            </ul>
            <Link
              to="/qualite-controle"
              data-testid="activity-qualite-link"
              className="mt-10 flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-qualite transition-colors group-hover:text-midnight"
            >
              Découvrir l'activité qualité
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </motion.article>
        </div>
      </div>
    </section>

    {/* VALEURS */}
    <section className="border-y border-line bg-cloud py-20 lg:py-28" data-testid="values-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeUp} transition={{ duration: 0.5 }} className="max-w-2xl">
          <p className="font-display text-xs font-bold uppercase tracking-[0.25em] text-steel">
            Ce qui nous engage
          </p>
          <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-midnight sm:text-4xl lg:text-5xl">
            La rigueur d'un partenaire technique de confiance
          </h2>
        </motion.div>
        <div className="mt-12 grid gap-px border border-line bg-line sm:grid-cols-2 lg:grid-cols-3" data-testid="values-grid">
          {VALUES.map((v, i) => (
            <motion.div
              key={v.title}
              {...fadeUp}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="bg-white p-8"
              data-testid={`value-${v.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              <v.icon className="h-7 w-7 text-midnight" />
              <h3 className="mt-5 font-display text-lg font-bold tracking-tight text-midnight">
                {v.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-steel">{v.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* DOUBLE CASQUETTE */}
    <section className="bg-white py-20 lg:py-28" data-testid="difference-section">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8">
        <motion.div {...fadeUp} transition={{ duration: 0.5 }}>
          <p className="font-display text-xs font-bold uppercase tracking-[0.25em] text-steel">
            Notre différence
          </p>
          <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-midnight sm:text-4xl">
            Le regard du contrôleur, les mains du technicien
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-steel">
            Parce que nos équipes réalisent elles-mêmes les travaux de
            raccordement, elles savent exactement ce qu'un contrôle qualité doit
            vérifier. Et parce qu'elles inspectent des infrastructures chaque
            semaine, elles exécutent leurs propres travaux avec le même niveau
            d'exigence.
          </p>
          <ul className="mt-8 space-y-4">
            <li className="flex items-start gap-4 border border-line p-4">
              <Wrench className="mt-0.5 h-5 w-5 shrink-0 text-terrain" />
              <p className="text-sm leading-relaxed text-midnight">
                <span className="font-display font-bold">Côté terrain :</span>{" "}
                raccordements FTTO/FTTH, soudure, raccordement des prises et interventions sur infrastructures.
              </p>
            </li>
            <li className="flex items-start gap-4 border border-line p-4">
              <FileSearch className="mt-0.5 h-5 w-5 shrink-0 text-qualite" />
              <p className="text-sm leading-relaxed text-midnight">
                <span className="font-display font-bold">Côté qualité :</span>{" "}
                inspections documentées, audits de conformité et remontées terrain exploitables.
              </p>
            </li>
          </ul>
        </motion.div>
        <motion.div {...fadeUp} transition={{ duration: 0.5, delay: 0.15 }} className="grid grid-cols-2 gap-px border border-line bg-line">
          <img src={FIBER_IMG} alt="Câbles fibre optique dans une baie de brassage" className="h-full w-full object-cover" data-testid="difference-fiber-image" />
          <img src={QUALITY_IMG} alt="Contrôle qualité documenté sur le terrain" className="h-full w-full object-cover" data-testid="difference-quality-image" />
          <img src={INFRA_IMG} alt="Infrastructure télécoms" className="col-span-2 h-56 w-full object-cover" data-testid="difference-infra-image" />
        </motion.div>
      </div>
    </section>

    {/* ZONE */}
    <section className="border-t border-line bg-white" data-testid="zone-section">
      <div className="bg-blueprint">
        <div className="mx-auto flex max-w-7xl flex-col items-start gap-6 px-4 py-16 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
          <motion.div {...fadeUp} transition={{ duration: 0.5 }}>
            <p className="font-display text-xs font-bold uppercase tracking-[0.25em] text-steel">
              Zone d'intervention
            </p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-midnight sm:text-4xl">
              Une seule zone : la France entière.
            </h2>
            <p className="mt-3 max-w-xl text-steel">
              Nos équipes se déplacent sur l'ensemble du territoire pour vos
              raccordements, inspections et audits.
            </p>
          </motion.div>
          <motion.div {...fadeUp} transition={{ duration: 0.5, delay: 0.15 }} className="flex items-center gap-4 border border-midnight bg-white px-8 py-6">
            <MapPin className="h-10 w-10 text-terrain" />
            <div>
              <p className="font-display text-2xl font-extrabold text-midnight">100 %</p>
              <p className="text-sm text-steel">du territoire national couvert</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>

    <CtaBand />
  </>
);

export default Home;
